import { produce } from "immer";
import { hfInstance } from "../../Cells";
import { ColumnTypes, ProcessTypes } from "../../../../../api.app/controllers/constants";
import { rangeToHfFormat } from "../CellsHelper";
import Merge from "./Merge";
import { arrayMoveImmutable } from "../../../utils/array";

const utils = (context) => {
  const { current } = context;
  return {
    refineRangeForAddition(range, activeCellIndex) {
      const isGrid = current.type === ProcessTypes.Grid;
      if (isGrid) {
        return this._shrinkRangeByColumnType(range, activeCellIndex, ColumnTypes.Grid);
      } else {
        let type;
        for (let i = range[0]; i <= range[2]; i++) {
          if (!type && [ColumnTypes.ColumnGroup, ColumnTypes.Player].includes(current.design.columns[i].type)) {
            type = current.design.columns[i].type;
            break;
          }
        }
        return this._shrinkRangeByColumnType(range, activeCellIndex, type ?? ColumnTypes.ColumnGroup);
      }
    },
    _shrinkRangeByColumnType(range, activeCellIndex, type) {
      let start, end;
      for (let i = range[0]; i <= range[2]; i++) {
        if (!start && current.design.columns[i].type === type) start = i;
        if (current.design.columns[i].type === type) end = i;
      }
      if (!start) {
        start = end = current.design.columns.findIndex((item) => item.type === type);
        activeCellIndex[0] = start;
      }

      return [[start, range[1], end, range[3]], activeCellIndex, type];
    },
  };
};
const main = (context) => {
  const { dispatch, currentSheet, currentSheetId, currentSheetName, current } = context;
  return {
    addColumnsToState(index, count, type, newColumnIds) {
      var newColumns = [];
      for (let i = 0; i < count; i++) newColumns.push({ id: newColumnIds[i], type });
      current.design.columns = produce(current.design.columns, (columns) => {
        columns.splice(index, 0, ...newColumns);
      });
      dispatch("cells/calculateColumnMap", null, { root: true });
      dispatch("cells/addRowColumnMap", { index, columns: newColumns }, { root: true });

      // Undo Operation
      return async () => {
        const columnIds = newColumns.map((item) => item.id);
        current.design.columns = produce(current.design.columns, (columns) => {
          columns.splice(index, count);
        });
        dispatch("cells/calculateColumnMap", null, { root: true });
        dispatch("cells/removeRowColumnMap", { columnIds }, { root: true });
        await dispatch("cells/removeNodesFromCellsContent", { columnIds: newColumnIds }, { root: true });
      };
    },
    addColumnsToHf(index, count) {
      hfInstance.addColumns(currentSheetId, [index, count]);

      return () => {
        hfInstance.removeColumns(currentSheetId, [index, count]);
      };
    },
    async removeColumnsFromState(index, count) {
      const columnIndexes = new Array(count).fill(0).map((item, i) => index + i);
      Merge(context).utils.recalculateMergedCells({ columnIndexes });
      const prevColumns = current.design.columns.slice(index, index + count);
      const prevColumnIds = prevColumns.map((column) => column.id);
      current.design.columns = produce(current.design.columns, (columns) => {
        columns.splice(index, count);
      });
      dispatch("cells/calculateColumnMap", null, { root: true });
      await dispatch("cells/removeRowColumnMap", { columnIds: prevColumnIds }, { root: true });

      await dispatch("cells/removeNodesFromCellsContent", { columnIds: prevColumnIds }, { root: true });

      return async () => {
        current.design.columns = produce(current.design.columns, (columns) => {
          columns.splice(index, 0, ...prevColumns);
        });
        dispatch("cells/calculateColumnMap", null, { root: true });
        dispatch("cells/addRowColumnMap", { index, columns: prevColumns }, { root: true });
        Merge(context).utils.recalculateMergedCells({ columnIndexes });
      };
    },
    async removeColumnsFromHf(range) {
      const count = range[2] - range[0] + 1;
      const index = range[0];
      const prevCellContents = hfInstance.getRangeSerialized(rangeToHfFormat(currentSheet, range));
      hfInstance.removeColumns(currentSheetId, [index, count]);

      return async () => {
        hfInstance.addColumns(currentSheetId, [index, count]);
        hfInstance.setCellContents({ sheet: currentSheetId, row: range[1], col: range[0] }, prevCellContents);

        await dispatch("cells/persistSelectedCells", { range, value: prevCellContents }, { root: true });
      };
    },
    async removeColumnLinks(index, count) {
      const columnIndexes = new Array(count).fill(0).map((item, i) => index + i);

      const previousLinks = { ...current.design.links };
      const entries = Object.entries(current.design.links).filter(([key, value]) => {
        return !columnIndexes.some((index) => {
          const columnId = current.design.columns[index].id;
          return value.cellId.includes(columnId);
        });
      });
      current.design.links = Object.fromEntries(entries);
      await dispatch("cells/persistDimensions", "links", { root: true });
      dispatch("smartSearch/loadProcessPlayers", { team: current.team, links: current.design.links }, { root: true });
      return () => {
        current.design.links = previousLinks;
        dispatch("smartSearch/loadProcessPlayers", { team: current.team, links: current.design.links }, { root: true });
      };
    },
    async addLink(ids, type, cellIds) {
      const prevMap = { ...current.design.links };
      const links = ids.reduce((acc, id, index) => {
        acc[id] = {
          cellId: cellIds[index],
          type,
        };
        return acc;
      }, {});

      current.design.links = produce(current.design.links, (linksMap) => {
        Object.assign(linksMap, links);
      });
      dispatch("smartSearch/loadProcessPlayers", { team: current.team, links: current.design.links }, { root: true });
      await dispatch("cells/persistDimensions", "links", { root: true });
      return async () => {
        current.design.links = prevMap;
        dispatch("smartSearch/loadProcessPlayers", { team: current.team, links: current.design.links }, { root: true });
        await dispatch("cells/persistDimensions", "links", { root: true });
      };
    },
    async editLink(newPlayerId, type, cellId) {
      const prevMap = { ...current.design.links };

      current.design.links = produce(current.design.links, (links) => {
        // Remove previous link related to cellId
        const [previousPlayerId] = Object.entries(links).find(([key, value]) => value.cellId === cellId);
        if (previousPlayerId) {
          delete links[previousPlayerId];
        }

        // Add new link
        links[newPlayerId] = {
          cellId,
          type,
        };
      });

      dispatch("smartSearch/loadProcessPlayers", { team: current.team, links: current.design.links }, { root: true });
      await dispatch("cells/persistDimensions", "links", { root: true });
      return async () => {
        current.design.links = prevMap;
        dispatch("smartSearch/loadProcessPlayers", { team: current.team, links: current.design.links }, { root: true });
        await dispatch("cells/persistDimensions", "links", { root: true });
      };
    },
    async moveColumnsFromState(source, target, count) {
      const columnsIdsToMove = new Set(current.design.columns.slice(source, source + count).map((column) => column.id));
      const targetColumnIds = new Set(current.design.columns.slice(target, target + count).map((column) => column.id));
      const allColumnIds = new Set([...targetColumnIds, ...columnsIdsToMove]);
      current.design.columns = arrayMoveImmutable(current.design.columns, source, target, count);
      await dispatch("cells/calculateColumnMap", null, { root: true });

      let updatedColumns = [];
      updatedColumns = Array.from(allColumnIds)
        .map((id) => current.design.columnMap[id])
        .filter((column) => column !== undefined);
      await dispatch("cells/persistMoveColumns", { updatedColumns }, { root: true });

      return async () => {
        current.design.columns = arrayMoveImmutable(current.design.columns, target, source, count);
        dispatch("cells/calculateColumnMap", null, { root: true });

        const updatedColumns = [];
        for (const id of allColumnIds) {
          if (current.design.columnMap[id]) {
            updatedColumns.push(current.design.columnMap[id]);
          }
        }
        await dispatch("cells/persistMoveColumns", { updatedColumns }, { root: true });
      };
    },
    async moveColumnsFromHf(source, target, count) {
      // in hyperformula target is the column before which a column is added
      hfInstance.moveColumns(currentSheetId, source, count, target > source ? target + count : target);
      return () => {
        this.moveColumnsFromHf(target, source, count);
      };
    },
  };
};

export default (context) => {
  return {
    main: main(context),
    utils: utils(context),
  };
};
